import { businessDiToFormData } from '@onefootprint/core';
import { postHostedBusinessVaultDecryptMutation } from '@onefootprint/fetch-hosted';
import { Form, useToast } from '@onefootprint/ui';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { type StepUpContext, useStepUp } from '@/idv2/hooks/use-step-up';
import { getLogger } from '@/idv2/utils/logger';
const {
  logInfo,
  logWarn
} = getLogger({
  name: 'idv2',
  location: 'tin-reveal-field'
});
type TinRevealFieldProps = {
  isScrubbed: boolean;
  authToken: string;
  stepUpContext: StepUpContext;
};
export const TinRevealField = ({
  isScrubbed,
  authToken,
  stepUpContext
}: TinRevealFieldProps) => {
  const {
    t
  } = useTranslation('idv2', {
    keyPrefix: 'collectBusinessData.fields'
  });
  const {
    t: tReveal
  } = useTranslation('idv2', {
    keyPrefix: 'collectBusinessData.tinReveal'
  });
  const {
    toast
  } = useToast();
  const decryptMutation = useMutation(postHostedBusinessVaultDecryptMutation());
  const {
    canStepUp,
    stepUp,
    isLoading: isStepUpLoading
  } = useStepUp({
    authToken,
    stepUpContext
  });
  const handleReveal = async (): Promise<string | undefined> => {
    logInfo('User initiated TIN reveal via step-up');
    try {
      const steppedUpToken = await stepUp();
      logInfo('Step-up successful, decrypting TIN');
      const {
        'business.tin': tinValue
      } = await decryptMutation.mutateAsync({
        headers: {
          'X-Fp-Authorization': steppedUpToken
        },
        body: {
          fields: ['business.tin']
        }
      });
      if (tinValue && typeof tinValue === 'string') {
        logInfo('TIN decrypted successfully');
        return tinValue;
      }
      logWarn('TIN decryption returned empty or invalid value');
      toast({
        title: tReveal($_1 => $_1.error.title),
        description: tReveal($_2 => $_2.error.description),
        variant: 'error'
      });
      return undefined;
    } catch (error) {
      logWarn('TIN reveal failed', error);
      toast({
        title: tReveal($ => $.error.title),
        description: tReveal($_0 => $_0.error.description),
        variant: 'error'
      });
      return undefined;
    }
  };
  const isLoading = isStepUpLoading || decryptMutation.isPending;

  // When scrubbed, the value already exists in the vault - don't require it
  // The user can either keep the existing value or reveal & edit it
  const rules = isScrubbed ? {} : {
    required: t($_3 => $_3.tin.errors.required)
  };
  return <Form.Ein name={businessDiToFormData('business.tin')} data-dd-privacy="mask" label={t($_4 => $_4.tin.label)} placeholder={t($_5 => $_5.tin.placeholder)} hint={t($_6 => $_6.tin.hint)} isScrubbed={isScrubbed} onReveal={canStepUp ? handleReveal : undefined} isRevealing={isLoading} rules={rules} />;
};